import { Box, Stack, Typography } from '@mui/material'
import { CreativeUploadFolder, useCreativeUploadPage } from './_main'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { buildInternalUrl } from 'utils/hooks'
import { isDeliveredVisual } from './_main/creativeUploadGating'
import { useUserData } from 'components/contexts/UserDataContext'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { BKBNThemeWrapper } from 'components/common/BKBNThemeWrapper'
import { GRAY_800 } from 'constants/styling/theme'
import { LessThanRequiredVisualsReason } from 'models/assignment'
import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import { MUIRadio } from 'components/common/MUIRadio'
import Modal from 'components/common/Modals/Modal/Modal'
import { ProductKind } from 'constants/product'
import { QueryStatus } from 'components/common/QueryStatus'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import { navigateBackOrHome } from 'utils/helpers'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useSubmitAssignment } from 'dataQueries'

/** Props for CT upload submit modal */
interface Props {
  /** Whether the modal is open or not */
  isOpen: boolean
  /** onClose handler fnc */
  onClose: () => void
}

/**
 * Modal for submitting uploaded visuals
 */
export const CreativeUploadSubmitModal: FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation(['ct_upload_page', 'enums', 'common'])
  const navigate = useNavigate()
  const allowInternalGuidelines = useFeatureFlag(FeatureFlag.ALLOW_CT_GUIDELINES_PAGE)
  const { baseUserData } = useUserData()
  const { spawnSuccessToast } = useSnackbar()

  const submitMutation = useSubmitAssignment()

  const {
    requiredVisualAmount,
    uploadedRequiredVisualsAmount,
    areVisualsMissing,
    assignmentId,
    assignmentData,
    assignmentProduct,
    assignmentQuery,
    fileCtrl,
  } = useCreativeUploadPage()

  const [insufficiencyReason, setInsufficiencyReason] = useState<LessThanRequiredVisualsReason>()
  const [insufficiencyComment, setInsufficiencyComment] = useState('')
  // undefined until the creative edits the box: a comment saved elsewhere (Lightroom plugin) is shown, and an untouched submit sends null, which the backend skips
  const [editedComment, setEditedComment] = useState<string>()
  const comment = editedComment ?? assignmentData?.creativeCommentsForAdmin ?? ''
  const [guidelinesCompliance, setGuidelinesCompliance] = useState<0 | 1>()

  const refetchAssignment = assignmentQuery.refetch
  useEffect(() => {
    if (isOpen) refetchAssignment()
  }, [isOpen, refetchAssignment])

  const isFormValid = useMemo(() => {
    if (!areVisualsMissing) {
      if (guidelinesCompliance === undefined) return false
      // the saved comment is a general note, not the explanation the Yes answer asks for
      if (guidelinesCompliance === 0 && !editedComment?.trim()) return false
    } else {
      if (!insufficiencyReason || (insufficiencyReason === LessThanRequiredVisualsReason.OTHER && !insufficiencyComment)) return false
    }

    return true
  }, [editedComment, guidelinesCompliance, insufficiencyComment, insufficiencyReason, areVisualsMissing])

  const reasons = useMemo(
    () => Object.values(LessThanRequiredVisualsReason).filter(
      (reason) => reason !== LessThanRequiredVisualsReason.DRONE_CANT_DO_BRACKETING || assignmentProduct?.kind === ProductKind.DRONE_PHOTO
    ),
    [assignmentProduct?.kind]
  )

  const handleSubmit = useCallback(() => {
    if (!isFormValid) return

    const requiredFilenames = fileCtrl.filesByTag[CreativeUploadFolder.REQUIRED_VISUALS]?.filter(isDeliveredVisual).filter((file) => !!file.gcFilename).map((file) => file.gcFilename as string) ?? []
    if (!requiredFilenames.length) return

    const additionalFilenames = fileCtrl.filesByTag[CreativeUploadFolder.ADDITIONAL_VISUALS]?.filter(isDeliveredVisual).filter((file) => !!file.gcFilename).map((file) => file.gcFilename as string) ?? []

    if (!assignmentProduct) {
      console.warn('Submit fnc rejected: missing assignmentProduct')
      return
    }

    submitMutation.mutate(
      {
        assignmentId,
        requiredVisualsFilenames: requiredFilenames,
        additionalVisualsFilenames: additionalFilenames,
        creativeCommentForBackbone: editedComment ?? null,
        product: assignmentProduct,
        minUploadCount: requiredVisualAmount,
        lessThanRequiredVisualsReason: areVisualsMissing
          ? {
            reason: insufficiencyReason!,
            comment: insufficiencyComment!,
          }
          : undefined
      },
      {
        onSuccess: () => {
          navigateBackOrHome(navigate)
          spawnSuccessToast(t('modal.success_toast'))
        },
      }
    )
  }, [assignmentId, assignmentProduct, editedComment, fileCtrl.filesByTag, insufficiencyComment, insufficiencyReason, isFormValid, navigate, requiredVisualAmount, spawnSuccessToast, submitMutation, t, areVisualsMissing])

  return (
    <BKBNThemeWrapper>
      <Modal
        contentStyle={{ maxWidth: '60rem' }}

        isOpen={isOpen}
        title={t(areVisualsMissing ? 'modal.title_missing' : 'modal.title_normal')}
        onClose={() => onClose()}
        afterClosed={() => {
          setInsufficiencyComment('')
          setInsufficiencyReason(undefined)
          setEditedComment(undefined)
          setGuidelinesCompliance(undefined)
        }}
        footerContent={(
          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={1} maxWidth="60rem">

            {areVisualsMissing &&
              <Typography flex="1 1 auto" variant="bodySmall">
                {t('modal.disclaimer')}
              </Typography>
            }

            <MUIButton
              type="secondaryBorder"
              onClick={() => onClose()}
            >
              {t('common:Cancel')}
            </MUIButton>

            <Box flex="0 0 auto">
              <MUIButton
                onClick={() => handleSubmit()}
                disabled={!isFormValid || assignmentQuery.isFetching || assignmentQuery.isError}
                isLoading={submitMutation.isPending}
              >
                {t('submit')}
              </MUIButton>
            </Box>

          </Stack>
        )}
      >
        <Stack gap={2}>

          {!areVisualsMissing &&
            <>

              <Stack gap={1}>

                <Typography variant="text-sm">
                  <Trans t={t} i18nKey="modal.guidelines_label">
                    {allowInternalGuidelines || assignmentData?.creativeInstructionsUrl
                      ? (
                        <a
                          href={allowInternalGuidelines
                            ? buildInternalUrl(assignmentProduct?.kind, assignmentData?.creativeInstructionsPairingId, baseUserData?.language ?? 'en')
                            : assignmentData?.creativeInstructionsUrl
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: GRAY_800, textDecoration: 'none' }}
                        >
                          &nbsp;
                        </a>
                      )
                      : <>&nbsp;</>
                    }
                  </Trans>
                </Typography>

                <SelectableButtonsGrid<0 | 1> values={[
                  {
                    value: 0,
                    displayName: t('common:Yes'),
                  },
                  {
                    value: 1,
                    displayName: t('common:No')
                  },
                ]}
                  selectedValue={guidelinesCompliance}
                  onSelect={(value) => setGuidelinesCompliance(value)}
                  preventUnselect
                />

              </Stack>

              <MUIInputField
                label={t('modal.comment_label')}
                isMultiline
                value={comment}
                disabled={assignmentQuery.isFetching}
                required={guidelinesCompliance === 0}
                onChange={(e) => setEditedComment(e.target.value)}
              />

            </>
          }

          {areVisualsMissing &&
            <>

              <Typography variant="text-md">
                <Trans
                  t={t}
                  i18nKey="modal.missing_visuals_info"
                  values={{
                    uploaded: uploadedRequiredVisualsAmount,
                    required: requiredVisualAmount,
                  }}
                >
                  &nbsp;
                </Trans>
              </Typography>

              <Stack gap={1}>

                <Typography variant="text-md" fontSize="1.3rem">
                  {t('modal.reason_label')}
                </Typography>

                {reasons.map((reason) => (
                  <MUIRadio
                    key={reason}
                    label={t(`enums:less_than_required_visuals_reason.${reason}`)}
                    checked={insufficiencyReason === reason}
                    onChange={() => setInsufficiencyReason(reason)}
                  />
                ))}

              </Stack>

              <MUIInputField
                label={t('modal.reason_comment_label')}
                isMultiline
                required={insufficiencyReason === LessThanRequiredVisualsReason.OTHER}
                value={insufficiencyComment}
                onChange={(e) => setInsufficiencyComment(e.target.value)}
              />

            </>
          }

        </Stack>

        <QueryStatus
          query={assignmentQuery.isError ? assignmentQuery : submitMutation}
          showStates={['error']}
          spaceTopRem={2}
          spaceBottomRem={2}
        />

      </Modal>
    </BKBNThemeWrapper>
  )
}
